import React, { useState, useEffect, useContext } from "react";
import styled from "styled-components";
import Request from "common/Request";
import * as d3 from "d3";
import {
  parseTrafficCongestion,
  parseLatencyReduction,
  parseRetransmissionsReduction,
  parseStatsThrougputData,
} from "./api";
import { parseStatsLatencyData } from "views/Statistics/System/StatsLatency/api";
import { parseStatsRtxData } from "views/Statistics/System/StatsRtx/api";
import RangeSliderInput from "common/RangeSliderInput";
import ActionsContext from "common/ActionsContext";
import SelectInputWithIcon from "common/SelectInputWithIcon";
import { hoursScopeChoices } from "common/constants";
import { formats } from "common/graphs/NVStackedAreaChart";
import { retrieveRange } from "common/api";
import Charts from "./CongestionCharts";
import { TileContainer, timeCoeffMap } from "./utils";

const SelectWrapper = styled.div`
  width: 150px;
  margin-bottom: 15px;

  @media screen and (min-width: 450px) {
    & {
      position: absolute;
      top: 17px;
      right: 50px;
      display: flex;
      align-items: center;
      margin-bottom: 0;
    }
  }
`;

const SingleColumn = styled.div`
  flex: 1 1 100%;
  display: flex;
  flex-flow: column;
  width: 100%;
`;

const parseToRoundedTimes = (items, hours) => {
  const coeff = timeCoeffMap[hours];
  const roundedTimesItems = items.map((item) => {
    const { time } = item;
    const roundedtime = new Date(Math.round(time.getTime() / coeff) * coeff);
    return { ...item, time: roundedtime };
  });
  return { items: roundedTimesItems };
};

const loadStatistics = ({ lines = 10, hours }) => {
  return Promise.all([
    ifCl
      .run(`show statistics congestion lines ${lines}`)
      .then(parseTrafficCongestion),
    ifCl
      .run(`show statistics latency lines ${lines}`)
      .then(parseStatsLatencyData),
    ifCl
      .run(`show statistics retransmission lines ${lines}`)
      .then(parseStatsRtxData),
    ifCl
      .run(`show statistics interface speed lines ${lines}`)
      .then(parseStatsThrougputData),
  ]).then(([congestion, latency, retransmission, throughput]) => {
    return ({
      congestion: parseToRoundedTimes(congestion.items, hours),
      latency: parseToRoundedTimes(latency.items, hours),
      retransmission: parseToRoundedTimes(retransmission.items, hours),
      throughput: parseToRoundedTimes(throughput.items, hours),
    })
  });
};

const linesFromHours = (hours) => hours * (60 / 5);

const CongestionReport = () => {
  const actions = useContext(ActionsContext);
  const [hours, setHours] = useState(24);
  const [request, setRequest] = useState(null);
  const [range, setRange] = useState();
  const [initialRange, setInitialRange] = useState();

  const doRequest = (hours) => {
    return retrieveRange(hours).then(({ from, to }) => {
      setInitialRange({ from, to });
      return loadStatistics({ hours, lines: linesFromHours(hours) });
    });
  };

  const doLoad = () => {
    setRequest(doRequest(hours));
  };

  useEffect(() => {
    setRange(undefined);
    doLoad();

    return actions.recv("do-load", doLoad);
  }, [hours]);

  const handleSelecetHoursChange = ({ target }) => {
    setHours(parseInt(target.value));
  };

  return (
    <SingleColumn>
      <SelectWrapper>
        <SelectInputWithIcon
          title="Date Range"
          name="Date Range"
          icon="date_range"
          selected={hours}
          onChange={handleSelecetHoursChange}
          options={hoursScopeChoices}
        />
      </SelectWrapper>
      {request !== null ? (
        <Request during={request}>
          {(result) => (
            <Charts {...result} range={range || initialRange} hours={hours} />
          )}
        </Request>
      ) : null}
      <TileContainer className="full-width can-compress">
        {initialRange ? (
          <RangeSliderInput
            {...initialRange}
            onChange={(value) => setRange(value)}
          />
        ) : null}
      </TileContainer>
    </SingleColumn>
  );
};

export default CongestionReport;
